/**
 * Run Program poller - keeps `program/programs.ts` honest about which
 * programs are registered on each machine and which are running.
 *
 * Shaped like `obs/scenePoll.ts` (only bridges a Run Program button on the
 * CURRENT page points at, only while the page is visible, one bridge at a
 * time) but at a much faster cadence, because the cost that forced OBS to
 * 20s does not exist here. `GET /programs` is answered from OPBridge's
 * config snapshot plus an in-memory running set - no socket, no process
 * table walk - so it is as cheap as the toggle and engine polls.
 *
 * Why it polls at all: a program's RUNNING state changes with nobody on our
 * side pressing anything - the operator quits it on that machine, or it
 * crashes - and OPBridge does not push that. The poll is what turns the
 * light off.
 */

import { bridges, getConnection, bridgeStateKey } from '../opbridge/store.ts';
import { layoutItems } from '../layout/store.ts';
import { OpbridgeError } from '../opbridge/httpClient.ts';
import { recordPrograms, recordProgramsFailure, programState } from './programs.ts';

/** Poll cadence while at least one Run Program button is on the page. */
const POLL_INTERVAL_MS = 3_000;

/** Wait when nothing needs asking. */
const IDLE_INTERVAL_MS = 4_000;

let timer: ReturnType<typeof setTimeout> | null = null;
let stopped = false;

/** Start the poller. Idempotent. */
export function startProgramPoll(): void {
  if (timer !== null) return;
  stopped = false;
  schedule(0);
}

/** Stop the poller. Used by tests + on tear-down. */
export function stopProgramPoll(): void {
  stopped = true;
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

function schedule(delayMs: number): void {
  if (stopped) return;
  timer = setTimeout(() => {
    timer = null;
    void tick();
  }, delayMs);
}

/**
 * Every bridge id a Run Program button on the CURRENT page points at. A
 * button with no `bridgeIds` contributes nothing: it is refused at dispatch
 * and says "pick a computer", and reading the default bridge's list under it
 * would show the WRONG machine's programs.
 *
 * Returns LOCAL ROW ids - see `bridgeStateKey` for why a raw `bridgeIds`
 * entry (a mesh node id) must never be used as the key.
 */
export function programBridgeIdsInUse(): string[] {
  const ids = new Set<string>();
  for (const item of layoutItems()) {
    if (item.kind !== 'button') continue;
    if (item.actionType !== 'runProgram') continue;
    for (const id of item.bridgeIds ?? []) ids.add(bridgeStateKey(id));
  }
  return Array.from(ids);
}

/**
 * Read one bridge's program list now and record the outcome. Never throws.
 * Exported for the Inspector (open / Refresh) and for dispatch (a 404 on
 * launch re-reads the list so the face can say WHY).
 */
export async function refreshPrograms(bridgeId: string): Promise<void> {
  if (!bridgeId) return;
  const conn = getConnection(bridgeId);
  if (!conn) {
    recordProgramsFailure(bridgeId, 'error', 'No such bridge.');
    return;
  }
  if (conn.status() !== 'connected') {
    recordProgramsFailure(bridgeId, 'error', `Bridge is ${conn.status()}.`);
    return;
  }
  try {
    const res = await conn.httpClient().programs();
    recordPrograms(bridgeId, Array.isArray(res?.programs) ? res.programs : []);
  } catch (err) {
    const status = err instanceof OpbridgeError ? err.httpStatus : undefined;
    const msg = err instanceof Error ? err.message : String(err);
    if (status === 401) {
      recordProgramsFailure(bridgeId, 'unauthorized',
        'OPBridge has not granted OPButtons the /programs scope.');
    } else if (status === 404) {
      recordProgramsFailure(bridgeId, 'error',
        'This OPBridge is too old to have Program Launch.');
    } else {
      recordProgramsFailure(bridgeId, 'error', msg);
    }
  }
}

/**
 * Ask EVERY connected bridge for its program list, once. Capability
 * discovery for the Computer picker ("3 programs" / "no programs yet") and
 * for the auto-pick. Called from the Inspector while it is open. Skips
 * bridges already read OK - keeping those current is the poll's job.
 */
export async function discoverProgramBridges(): Promise<void> {
  for (const cfg of bridges()) {
    if (stopped) return;
    const conn = getConnection(cfg.id);
    if (!conn || conn.status() !== 'connected') continue;
    if (programState(cfg.id).status === 'ok') continue;
    await refreshPrograms(cfg.id);
  }
}

function pageIsVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

async function tick(): Promise<void> {
  if (stopped) return;
  if (!pageIsVisible()) {
    schedule(IDLE_INTERVAL_MS);
    return;
  }
  const wanted = programBridgeIdsInUse();
  if (wanted.length === 0 || bridges().length === 0) {
    schedule(IDLE_INTERVAL_MS);
    return;
  }
  for (const id of wanted) {
    if (stopped) return;
    await refreshPrograms(id);
  }
  schedule(POLL_INTERVAL_MS);
}
